import { LocalStorageService } from '@aiz-gl/omni-ui';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, IDToken } from '@okta/okta-auth-js';
import { jwtDecode } from 'jwt-decode';
import {
  BehaviorSubject,
  catchError,
  filter,
  lastValueFrom,
  map,
  throwError,
} from 'rxjs';
import {
  AuthProfile,
  AuthResponse,
  AuthTokenDecoded,
} from '../../models/auth.model';
import { CasbinPermissionsService } from '../feature/casbin-permissions.service';

interface PolicyAccessDeniedState {
  reason: 'policy-load-failed';
  status?: number;
  dccCode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oktaStateService = inject(OktaAuthStateService);
  private oktaAuth = inject(OKTA_AUTH);
  private permissionsService = inject(CasbinPermissionsService);
  private localStore = inject(LocalStorageService);
  private initializationPromise?: Promise<boolean>;

  private initialized$ = new BehaviorSubject<boolean>(false);

  http = inject(HttpClient);

  oktaState$ = this.oktaStateService.authState$.pipe(
    filter((authState: AuthState) => !!authState),
  );

  oktaState = toSignal(this.oktaState$, {
    initialValue: { isAuthenticated: false } as AuthState,
  });

  readonly accessToken = signal<string | undefined>(undefined);
  readonly policyAccessDenied = signal<PolicyAccessDeniedState | null>(null);
  readonly currentPolicyDcc = signal<string | null>(null);
  readonly idToken = computed(() => this.oktaState().idToken);

  readonly oktaAuthenticated = computed(
    () => this.oktaState().isAuthenticated || false,
  );

  readonly hasAccessToken = computed(() => !!this.accessToken());

  readonly isAuthenticated = computed(
    () => this.oktaAuthenticated() && this.hasAccessToken(),
  );

  readonly isPolicyAccessDenied = computed(() => !!this.policyAccessDenied());

  readonly userName = computed(
    () =>
      (this.oktaAuthenticated() && this.oktaState().idToken?.claims.name) || '',
  );

  readonly userEmail = computed(
    () =>
      (this.oktaAuthenticated() && this.oktaState().idToken?.claims.email) ||
      '',
  );

  readonly userIdToken = computed(
    () =>
      localStorage.getItem('userIdToken') ||
      (this.oktaAuthenticated() && this.oktaState().idToken?.idToken) ||
      undefined,
  );

  overrideUserIdToken = (token: string | undefined) => {
    if (!token) return this.removeUserIdTokenOverride();
    localStorage.setItem('userIdToken', token);
    window.location.reload();
  };

  removeUserIdTokenOverride = () => {
    localStorage.removeItem('userIdToken');
    window.location.reload();
  };

  readonly authProfile = computed((): AuthProfile | undefined => {
    const token = this.accessToken();
    if (!token) return;
    try {
      const authToken = jwtDecode<AuthTokenDecoded>(token);

      const roles = Array.isArray(authToken.role)
        ? [...authToken.role]
        : authToken.role
          ? [authToken.role]
          : [];

      const businessEntities = Array.isArray(authToken.businessentities)
        ? [...authToken.businessentities]
        : authToken.businessentities
          ? [authToken.businessentities]
          : [];

      return {
        email: authToken.email,
        roles,
        businessEntities,
        isAdmin: authToken.admin?.toLowerCase() === 'true',
        token,
        tokenExpiration: authToken.exp,
        tokenExpirationDate: this.convertSecondsToDate(authToken.exp),
      };
    } catch (error) {
      console.warn('Failed to decode access token for auth profile.', error);

      return {
        email: this.userEmail() || this.oktaState().idToken?.claims.email || '',
        roles: [],
        businessEntities: [],
        isAdmin: false,
        token,
        tokenExpiration: 0,
        tokenExpirationDate: new Date(0),
      };
    }
  });

  readonly userRolesString = computed(
    () => this.authProfile()?.roles.join(',') || '',
  );
  readonly userBusinessEntitiesString = computed(
    () => this.authProfile()?.businessEntities.join(',') || '',
  );

  async signIn(): Promise<void> {
    await this.oktaAuth.signInWithRedirect();
  }

  async signOut(): Promise<void> {
    await this.oktaAuth.signOut().then(() => {
      // Clean Out Local Storage Here
      this.clearPolicyAccessDenied();
      this.currentPolicyDcc.set(null);
      this.accessToken.set(undefined);
    });
  }

  generateToken$ = (idToken: string) => {
    console.log('Generating Access Token with ID Token:', idToken);
    // Use the Okta ID Token to generate the access token used in other API calls.
    const headers = new HttpHeaders().set('X-Token', idToken || '');

    // TODO: Why is the url case sensitive?
    return this.http
      .post<AuthResponse>('v1/Auth/generate-token', { idToken }, { headers })
      .pipe(
        catchError((response: HttpErrorResponse) => {
          const { status, error } = response;

          if (status === 401) {
            console.warn(
              'Token generation returned 401 - token may be expired',
            );
          }
          return throwError(() => error);
        }),

        map((r) => {
          const { access_token } = r;
          this.accessToken.set(access_token);
          return r;
        }),
      );
  };

  private initializeSession(idToken: string): Promise<boolean> {
    if (!this.initializationPromise) {
      this.clearPolicyAccessDenied();
      this.initializationPromise = lastValueFrom(this.generateToken$(idToken))
        .then(async (response) => {
          try {
            await this.permissionsService.reloadPolicies(response.access_token);
            this.clearPolicyAccessDenied();
            this.initialized$.next(true);
          } catch (error) {
            console.error('Policy initialization failed:', error);
            this.handlePolicyAccessDenied(error);
            this.initialized$.next(false);
            this.currentPolicyDcc.set(null);
            this.permissionsService.clearPermissions();
          }

          return true;
        })
        .catch((error) => {
          console.error('Auth initialization failed:', error);
          this.clearPolicyAccessDenied();
          this.accessToken.set(undefined);
          this.initialized$.next(false);
          this.currentPolicyDcc.set(null);
          this.permissionsService.clearPermissions();
          return false;
        })
        .finally(() => {
          this.initializationPromise = undefined;
        });
    }

    return this.initializationPromise;
  }

  convertSecondsToDate(seconds: number) {
    return new Date(seconds * 1000);
  }

  async getAccessToken(): Promise<string | undefined> {
    // This method handles token renewal internally if the current token is expired
    return this.oktaAuth.getAccessToken();
  }

  async handleRedirect() {
    try {
      // Handle Token Storage
      await this.oktaAuth.handleLoginRedirect();
    } catch (error) {
      // Handle error (e.g., display error message)
      console.error('Login redirect error:', error);
    }
  }

  async getIdTokenFromTokenManager(): Promise<string | undefined> {
    const token = (await this.oktaAuth.tokenManager.get('idToken')) as
      | IDToken
      | undefined;
    return token?.idToken;
  }

  getIdTokenFromLocalStorage() {
    const tokenStorage = this.localStore.getItem<
      IDToken | { idToken?: IDToken | string } | undefined
    >('okta-token-storage');

    if (!tokenStorage) {
      return '';
    }

    if (typeof tokenStorage === 'string') {
      return tokenStorage;
    }

    const nestedIdToken = tokenStorage.idToken;

    if (typeof nestedIdToken === 'string') {
      return nestedIdToken;
    }

    return nestedIdToken?.idToken || '';
  }

  private async resolveIdToken(): Promise<string> {
    const overrideToken = localStorage.getItem('userIdToken');
    if (overrideToken) {
      return overrideToken;
    }

    const stateToken = this.oktaState().idToken?.idToken;
    if (stateToken) {
      return stateToken;
    }

    const tokenManagerToken = await this.getIdTokenFromTokenManager();
    if (tokenManagerToken) {
      return tokenManagerToken;
    }

    return this.getIdTokenFromLocalStorage();
  }

  async initializeAuth(): Promise<boolean> {
    // If the session is already established, skip async token resolution entirely.
    // This prevents authGuard from redirecting to /login when called right after
    // LoginLandingComponent finishes auth (Okta signal may not have updated yet).
    if (this.accessToken() && this.initialized$.value) {
      return true;
    }

    const token = await this.resolveIdToken();
    if (!token) {
      this.clearPolicyAccessDenied();
      this.initialized$.next(false);
      return false;
    }

    return this.initializeSession(token);
  }

  async refreshPoliciesForDcc(dccCode?: string): Promise<boolean> {
    const token = this.accessToken();

    if (!token) {
      const initialized = await this.initializeAuth();
      if (!initialized) {
        return false;
      }
    }

    const accessToken = this.accessToken();
    if (!accessToken) {
      return false;
    }

    this.clearPolicyAccessDenied();

    try {
      await this.permissionsService.reloadPolicies(accessToken);

      if (
        dccCode &&
        !this.permissionsService.hasBusinessEntityAccess(dccCode)
      ) {
        this.policyAccessDenied.set({
          reason: 'policy-load-failed',
          dccCode,
        });
        return false;
      }

      this.currentPolicyDcc.set(dccCode || null);
      this.initialized$.next(true);
      return true;
    } catch (error) {
      this.currentPolicyDcc.set(null);
      this.handlePolicyAccessDenied(error, dccCode);
      return false;
    }
  }

  shouldRefreshPoliciesForDcc(dccCode?: string): boolean {
    return !!dccCode && this.currentPolicyDcc() !== dccCode;
  }

  clearPolicyAccessDenied(): void {
    this.policyAccessDenied.set(null);
  }

  getPolicyAccessDeniedQueryParams(dccCode?: string) {
    const denialState = this.policyAccessDenied();
    const deniedDccCode = dccCode || denialState?.dccCode;

    return {
      reason: 'policy-load-failed',
      ...(deniedDccCode ? { dccCode: deniedDccCode } : {}),
    };
  }

  private handlePolicyAccessDenied(error: unknown, dccCode?: string): void {
    const status = this.getErrorStatus(error);

    this.policyAccessDenied.set({
      reason: 'policy-load-failed',
      ...(typeof status === 'number' ? { status } : {}),
      ...(dccCode ? { dccCode } : {}),
    });
  }

  private getErrorStatus(error: unknown): number | undefined {
    if (!error || typeof error !== 'object') {
      return undefined;
    }

    const status = 'status' in error ? error.status : undefined;
    if (typeof status === 'number') {
      return status;
    }

    const statusCode = 'status_code' in error ? error.status_code : undefined;
    return typeof statusCode === 'number' ? statusCode : undefined;
  }

  isMockingData() {
    // Access the configuration options directly
    const oktaAuthWithOptions = this.oktaAuth as typeof this.oktaAuth & {
      options?: { clientId?: string };
    };
    const config = oktaAuthWithOptions.options;
    console.log('Current Okta Client ID:', config.clientId);

    if (config.clientId === 'fake-client-id') {
      console.warn('CONFIRMED: Using spoofed configuration.');

      return true;
    }
    return false;
  }
}
